import React, { useEffect, useState, useRef } from 'react';
import Swal from 'sweetalert2';
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import "../../App.css";
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import Menu from '../../panel/Menu';
import { Card, CardContent, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography, Button, TableContainer, Table, TableHead, TableRow, Paper, TableCell, TableBody, Divider, Box } from '@mui/material';

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

const animationStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(15px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .page-fade-in {
    animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  /* --- METICULOUS PRINT STYLES --- */
  @media print {
    @page {
        size: A4;
        margin: 5mm;
    }

    html, body, #root {
        background: #fff !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        color: #000 !important;
    }

    .no-print, nav, header, footer, .sidebar, button, .MuiTextField-root {
        display: none !important;
    }

    * {
        color: #000 !important;
        background: transparent !important;
        box-shadow: none !important;
        text-shadow: none !important;
        animation: none !important;
        border-color: #ccc !important;
    }

    .page-fade-in {
        padding: 0 !important;
        margin: 0 !important;
        max-width: 100% !important;
        width: 100% !important;
    }

    .print-area {
        border: none !important;
        background: #fff !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
    }

    table {
        width: 100% !important;
        border-collapse: collapse !important;
    }
    
    th, td {
        border: 1px solid #ddd !important;
        padding: 6px 8px !important;
        color: #000 !important;
    }

    th {
        background-color: #f5f5f5 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        font-weight: bold !important;
    }
  }
`;

function Paymodewise() {
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        background: colors.calPolyGreen,
        color: colors.vanilla
    });

    const printRef = useRef();

    const [loading, setloading] = useState(true);
    
    // Set default values to today's date
    const [fromDate, setfromDate] = useState(moment().format('YYYY-MM-DD'));
    const [toDate, settoDate] = useState(moment().format('YYYY-MM-DD'));

    const [total, settotal] = useState(0);
    const [paymodes, setpaymodes] = useState([]);
    const [propertyId, setPropertyId] = useState(null);
    const [userData, setUserData] = useState(null);

    /* 🔐 AUTH + PROPERTY FETCH */
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (user) => {
            if (!user) return;

            setUserData(user);

            const roleSnap = await getDoc(doc(db, "authroles", user.uid));
            if (roleSnap.exists()) {
                setPropertyId(roleSnap.data().propertyId);
            } else {
                Toast.fire({ icon: "error", title: "Property not assigned" });
            }
        });

        return () => unsub();
    }, []);

    // Auto-trigger report when propertyId is loaded
    useEffect(() => {
        if (propertyId && fromDate && toDate) {
            getReport();
        }
    }, [propertyId]);

    /* 📊 GET REPORT */
    const getReport = async () => {
        if (!fromDate || !toDate) {
            Toast.fire({ icon: "error", title: "Please select From & To Date" });
            return;
        }

        if (!propertyId) {
            Toast.fire({ icon: "error", title: "Property not assigned" });
            return;
        }

        setloading(true);
        setpaymodes([]);
        settotal(0);

        const startDate = new Date(fromDate);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(toDate);
        endDate.setDate(endDate.getDate() + 1);

        try {
            /* ✅ FETCH PAYMENT MODES */
            const paymentModesSnap = await getDocs(
                query(
                    collection(db, "paymentmodes"),
                    where("propertyId", "==", propertyId)
                )
            );

            const paymentModes = paymentModesSnap.docs.map(doc => ({
                id: doc.id,
                paymode: doc.data().paymode
            }));

            /* 🔁 PARALLEL QUERIES */
            const paymode1Queries = paymentModes.map(mode =>
                getDocs(query(
                    collection(db, "payhistory"),
                    where("propertyId", "==", propertyId),
                    where("paymode1", "==", mode.id),
                    where("createdAt", ">=", Timestamp.fromDate(startDate)),
                    where("createdAt", "<", Timestamp.fromDate(endDate))
                ))
            );

            const paymode2Queries = paymentModes.map(mode =>
                getDocs(query(
                    collection(db, "payhistory"),
                    where("propertyId", "==", propertyId),
                    where("paymode2", "==", mode.id),
                    where("createdAt", ">=", Timestamp.fromDate(startDate)),
                    where("createdAt", "<", Timestamp.fromDate(endDate))
                ))
            );

            const [snap1, snap2] = await Promise.all([
                Promise.all(paymode1Queries),
                Promise.all(paymode2Queries)
            ]);

            let grandTotal = 0;

            const reportData = paymentModes.map((mode, index) => {
                const total1 = snap1[index].docs.reduce(
                    (s, d) => s + (Number(d.data().amount1) || 0), 0
                );

                const total2 = snap2[index].docs.reduce(
                    (s, d) => s + (Number(d.data().amount2) || 0), 0
                );

                const totalAmount = total1 + total2;
                grandTotal += totalAmount;

                return {
                    paymode: mode.paymode,
                    totalamount: totalAmount
                };
            });

            setpaymodes(reportData);
            settotal(grandTotal);
        } catch (err) {
            console.error(err);
            Toast.fire({ icon: "error", title: "Error loading report" });
        } finally {
            setloading(false);
        }
    };

    /* 🖨 PRINT */
    const handlePrint = () => {
        window.print();
    };

    const handleFromDate = (e) => {
        setfromDate(e.target.value);
    };

    const handleToDate = (e) => {
        settoDate(e.target.value);
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }} className="page-fade-in">
            <style>{animationStyles}</style>
            <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
                <div style={{ backgroundColor: 'transparent' }}>
                    <Box sx={{ px: 1, p: 3 }}>

                        <Typography variant="h5" fontWeight={800} mb={3} sx={{ color: colors.vanilla }} className="no-print">
                            Paymode Wise Report
                        </Typography>

                        {/* FILTER CARD */}
                        <Card sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)", mb: 4 }} className="no-print">
                            <CardContent sx={{ p: 3 }}>
                                <div className="container-fluid p-0">
                                    <div className="row align-items-center">
                                        <div className="col-sm-4 mb-3 mb-sm-0">
                                            <TextField
                                                fullWidth
                                                size='small'
                                                type="date"
                                                label="From Date"
                                                value={fromDate}
                                                onChange={handleFromDate}
                                                InputLabelProps={{ shrink: true, style: { color: colors.vanilla } }}
                                                InputProps={{ style: { color: colors.vanilla } }}
                                                sx={{ 
                                                    "& .MuiOutlinedInput-root": { 
                                                        borderRadius: "8px",
                                                        backgroundColor: colors.darkGreen,
                                                        "& fieldset": { borderColor: colors.goldenrod },
                                                        "&:hover fieldset": { borderColor: colors.gold },
                                                        "&.Mui-focused fieldset": { borderColor: colors.gold }
                                                    } 
                                                }}
                                            />
                                        </div>

                                        <div className="col-sm-4 mb-3 mb-sm-0">
                                            <TextField
                                                fullWidth
                                                size='small'
                                                type="date"
                                                label="To Date"
                                                value={toDate}
                                                onChange={handleToDate}
                                                InputLabelProps={{ shrink: true, style: { color: colors.vanilla } }}
                                                InputProps={{ style: { color: colors.vanilla } }}
                                                sx={{ 
                                                    "& .MuiOutlinedInput-root": { 
                                                        borderRadius: "8px",
                                                        backgroundColor: colors.darkGreen,
                                                        "& fieldset": { borderColor: colors.goldenrod },
                                                        "&:hover fieldset": { borderColor: colors.gold },
                                                        "&.Mui-focused fieldset": { borderColor: colors.gold }
                                                    } 
                                                }}
                                            />
                                        </div>

                                        <div className="col-sm-4">
                                            <Button
                                                fullWidth
                                                size='small'
                                                variant="contained"
                                                disabled={loading}
                                                onClick={getReport}
                                                sx={{ 
                                                    bgcolor: colors.goldenrod, 
                                                    color: colors.darkGreen, 
                                                    fontWeight: 700, 
                                                    textTransform: "none", 
                                                    borderRadius: "8px",
                                                    height: "40px",
                                                    '&:hover': { bgcolor: colors.gold } 
                                                }}
                                            >
                                                {loading ? "Loading..." : "Get Report"}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* REPORT */}
                        <Card
                            elevation={0}
                            className="print-area"
                            ref={printRef}
                            sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Divider sx={{ mb: 3, borderColor: colors.goldenrod, opacity: 0.4 }} />

                                {/* DATE HEADER */}
                                <div className="container-fluid p-0 mb-3">
                                    <div className="row align-items-center text-center">
                                        <div className="col-sm-4 mb-2 mb-sm-0">
                                            <Typography variant="subtitle2" sx={{ color: colors.vanilla }}>
                                                From Date : {fromDate ? moment(fromDate).format("DD/MM/YYYY") : "—"}
                                            </Typography>
                                        </div>

                                        <div className="col-sm-4 mb-2 mb-sm-0">
                                            <Typography variant="h6" fontWeight="bold" sx={{ color: colors.gold }}>
                                                PAYMODE WISE REPORT
                                            </Typography>
                                        </div>

                                        <div className="col-sm-4">
                                            <Typography variant="subtitle2" sx={{ color: colors.vanilla }}>
                                                To Date : {toDate ? moment(toDate).format("DD/MM/YYYY") : "—"}
                                            </Typography>
                                        </div>
                                    </div>
                                </div>

                                <Divider sx={{ mb: 3, borderColor: colors.goldenrod, opacity: 0.4 }} />

                                {/* TABLE */}
                                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: `1px solid ${colors.goldenrod}`, backgroundColor: "transparent" }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: colors.darkGreen }}>
                                            <TableRow>
                                                <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}><b>Paymode</b></TableCell>
                                                <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}><b>Amount</b></TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {paymodes.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={2} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                                                        No paymode records found
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {paymodes.map((item, index) => (
                                                <TableRow key={index} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                                    <TableCell sx={{ color: colors.gold, fontWeight: 600, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{item.paymode}</TableCell>
                                                    <TableCell align="right" sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                                        {item.totalamount.toFixed(2)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}

                                            <TableRow sx={{ backgroundColor: colors.darkGreen }}>
                                                <TableCell sx={{ color: colors.gold, fontWeight: 700, borderBottom: 0 }}><b>Total</b></TableCell>
                                                <TableCell align="right" sx={{ color: colors.gold, fontWeight: 700, borderBottom: 0 }}>
                                                    <b>{total.toFixed(2)}</b>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>

                        {/* PRINT BUTTON */}
                        <Box textAlign="center" mt={3} className="no-print">
                            <Button
                                variant="contained"
                                onClick={handlePrint}
                                sx={{ 
                                    bgcolor: colors.goldenrod, 
                                    color: colors.darkGreen, 
                                    fontWeight: 700, 
                                    textTransform: "none", 
                                    borderRadius: "8px",
                                    px: 4,
                                    py: 1.2,
                                    '&:hover': { bgcolor: colors.gold } 
                                }}
                            >
                                Print Report
                            </Button>
                        </Box>

                    </Box>
                </div>
            </Box>
        </Box>
    );
}

export default Paymodewise;